// src/components/more/Videos.js
import React, { useState } from 'react';
import ReactPlayer from 'react-player';
import { ChevronLeft, Clock, Video, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { appConstants } from '../../atomstore/store';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

const Videos = () => {
    const navigate = useNavigate();
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [constants] = useAtom(appConstants);

    const getYoutubeId = (video) => {
        if (!video) return null;
        if (!video.youtubeLink || typeof video.youtubeLink !== 'string') return null;

        // Supports:
        // - https://www.youtube.com/watch?v=VIDEO_ID&t=2s
        // - https://youtu.be/VIDEO_ID?t=2
        const match = video.youtubeLink.match(
            /(?:youtube\.com\/watch\?[^#\s]*v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/
        );
        return match?.[1] || null;
    };


    const fallbackVideos = [
        { title: 'Welcome to Footprints', duration: '5:30', category: 'Introduction', youtubeLink: "https://www.youtube.com/watch?v=hcc4XFUtEWw" },
        { title: 'Our Curriculum Overview', duration: '8:15', category: 'Academics', youtubeLink: "https://www.youtube.com/watch?v=CUv1Wony6RE&t=2s" },

    ];

    const videos =
        Array.isArray(constants?.EducationVideos) && constants.EducationVideos.length && constants.EducationVideos[0]?.youtubeLink
            ? constants.EducationVideos
            : fallbackVideos;

    const getDisplayDuration = (video) => {
        return video.duration || '--:--';
    };

    const handlePlayVideo = async (video) => {
        const url = video?.youtubeLink;
        if (!url) return;

        // iOS: use Capacitor Browser (SFSafariViewController) instead of in-app embedded player
        if (Capacitor.getPlatform() === 'ios') {
            await Browser.open({ url });
            return;
        }

        setSelectedVideo(video);
    };

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>Parent Orientation</h2>
            </div>

            <div className="p-4 space-y-4">
                {/* Header card */}
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">Educational Resources</h3>
                    <p className="text-indigo-100">Learn about our programs & practices</p>
                </div>

                {/* Video list */}
                {videos.map((video, idx) => (
                    <div
                        key={idx}
                        className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
                    >
                        <div className="flex items-center space-x-4">
                            {getYoutubeId(video) ? (
                                <img
                                    src={`https://img.youtube.com/vi/${getYoutubeId(video)}/hqdefault.jpg`}
                                    alt={video.title}
                                    className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
                                />
                            ) : (
                                <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center text-3xl">
                                    {video.thumb || '🎬'}
                                </div>
                            )}

                            <div className="flex-1">
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-lg font-medium">
                                    {video.category}
                                </span>
                                <h3 className="font-bold text-gray-800 mt-1 mb-1">{video.title}</h3>
                                {!!video?.duration && (
                                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                                        <Clock className="w-4 h-4" />
                                        <span>{getDisplayDuration(video)}</span>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={() => handlePlayVideo(video)}
                                disabled={!video.youtubeLink}
                                className={`w-10 h-10 rounded-full flex items-center justify-center ${video.youtubeLink
                                    ? 'bg-purple-500'
                                    : 'bg-gray-300 cursor-not-allowed'
                                    }`}
                            >
                                <Video className="w-5 h-5 text-white" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Video player overlay */}
            {selectedVideo && (
                <div className="fixed inset-0 bg-black bg-opacity-90 flex flex-col items-center justify-center z-50 p-4">
                    <button
                        onClick={() => setSelectedVideo(null)}
                        className="absolute top-4 right-4 text-white p-2"
                    >
                        <X className="w-6 h-6" />
                    </button>

                    <div className="w-full max-w-3xl aspect-video">
                        <ReactPlayer
                            src={selectedVideo.youtubeLink}
                            controls
                            width="100%"
                            height="100%"
                            config={{
                                youtube: {
                                    referrerPolicy: 'strict-origin-when-cross-origin',
                                },
                            }}
                        />
                    </div>

                    <h3 className="text-white mt-4 text-lg font-semibold">
                        {selectedVideo.title}
                    </h3>
                </div>
            )}
        </div>
    );
};

export default Videos;
